import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  UploadCloud, FileText, Check, X, Database, Loader2, Plus, AlertCircle, CheckCircle2, Info, Eye,
} from "lucide-react";

const NA = () => (
  <span data-testid="not-available-badge" className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-semibold">
    <AlertCircle className="w-3 h-3" /> Not available
  </span>
);

export default function ProfilePage() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    full_name: "", phone: "", headline: "", current_company: "", key_achievement: "",
    portfolio_url: "", location: "", experience_years: "", skills: [],
  });
  const [resume, setResume] = useState({ filename: null, preview: "" });
  const [skillInput, setSkillInput] = useState("");
  const [linkedShared, setLinkedShared] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewingResume, setPreviewingResume] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/profile").then(({ data }) => {
      if (data.profile) {
        const p = data.profile;
        setForm({
          full_name: p.full_name || "",
          phone: p.phone || "",
          headline: p.headline || "",
          current_company: p.current_company || "",
          key_achievement: p.key_achievement || "",
          portfolio_url: p.portfolio_url || "",
          location: p.location || "",
          experience_years: p.experience_years ?? "",
          skills: p.skills || [],
        });
        setResume({ filename: p.resume_filename, preview: (p.resume_text || "").slice(0, 600), hasFile: !!p.resume_storage_path });
      }
      setLinkedShared(!!data.linked_from_shared_db);
    }).catch(() => {});
  }, []);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/profile/upload-resume", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResume({ filename: data.resume_filename, preview: data.preview, hasFile: true });
      toast.success(`Resume parsed (${data.chars.toLocaleString()} characters).`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const previewResume = async (e) => {
    e.stopPropagation();
    setPreviewingResume(true);
    try {
      const res = await api.get("/profile/resume/download", { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch {
      toast.error("Could not open resume.");
    } finally {
      setPreviewingResume(false);
    }
  };

  const canPreview = resume.hasFile || !!resume.filename || !!resume.preview || !!form.full_name.trim();

  const addSkill = () => {
    const s = skillInput.trim();
    if (s && !form.skills.includes(s)) setForm({ ...form, skills: [...form.skills, s] });
    setSkillInput("");
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/profile", {
        full_name: form.full_name || null,
        phone: form.phone || null,
        headline: form.headline || null,
        current_company: form.current_company || null,
        key_achievement: form.key_achievement || null,
        portfolio_url: form.portfolio_url || null,
        location: form.location || null,
        experience_years: form.experience_years === "" ? null : Number(form.experience_years),
        skills: form.skills,
      });
      toast.success("Profile saved.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "mt-1.5 bg-slate-50 border-slate-300 text-slate-900 focus-visible:ring-amber-500/40 h-11";
  const FieldLabel = ({ children, empty }) => (
    <div className="flex items-center gap-2">
      <Label className="text-slate-700 text-sm">{children}</Label>
      {empty && <NA />}
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="profile-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-display">Profile & Resume</h1>
        <p className="text-slate-500 mt-1">These details auto-fill your application emails. Fields synced from the main app appear here — edit any that show "Not available".</p>
      </div>

      {linkedShared && (
        <div className="flex items-center gap-2 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          <Database className="w-4 h-4" /> Synced from your main app profile — no re-upload needed.
        </div>
      )}

      {/* Resume upload */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold">Resume</h2>
          <span className="relative group inline-flex" data-testid="resume-info-icon">
            <Info className="w-4 h-4 text-slate-400 cursor-help" />
            <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-6 z-20 w-64 rounded-lg bg-slate-900 text-white text-xs px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
              This resume and your profile details are fetched from your main JobBasket app. Update them there to keep them in sync — or upload a new resume here anytime.
            </span>
          </span>
          {canPreview && (
            <button type="button" data-testid="resume-preview-eye-btn" onClick={previewResume} disabled={previewingResume}
              title="Preview the resume PDF sent with your applications"
              className="inline-flex items-center justify-center w-6 h-6 rounded-full text-amber-600 hover:text-amber-800 hover:bg-amber-50 transition-colors disabled:opacity-50">
              {previewingResume ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
            </button>
          )}
        </div>
        <div
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files[0]); }}
          data-testid="resume-dropzone"
          className="mt-4 border-2 border-dashed border-slate-300 hover:border-orange-500/50 rounded-2xl p-8 text-center cursor-pointer transition-colors"
        >
          <input ref={fileRef} type="file" accept=".pdf,.docx,.txt" className="hidden"
            data-testid="resume-file-input" onChange={(e) => upload(e.target.files[0])} />
          {uploading ? <Loader2 className="w-8 h-8 mx-auto text-amber-500 animate-spin" />
            : resume.filename ? <FileText className="w-8 h-8 mx-auto text-amber-500" />
            : <UploadCloud className="w-8 h-8 mx-auto text-slate-500" />}
          <p className="mt-3 text-sm font-medium">{resume.filename || "Drop your resume or click to upload"}</p>
          <p className="text-xs text-slate-500 mt-1">PDF, DOCX or TXT</p>
          {resume.filename && (
            <p data-testid="resume-attach-note" className="mt-2 text-xs text-emerald-600 font-medium flex items-center justify-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5" /> Automatically attached to every application email
            </p>
          )}
          {canPreview && (
            <button data-testid="resume-preview-btn" onClick={previewResume} disabled={previewingResume}
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 hover:text-amber-900 border border-amber-300 rounded-full px-3 py-1.5">
              {previewingResume ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />} Preview resume
            </button>
          )}
          {!resume.filename && canPreview && (
            <p data-testid="resume-autogen-note" className="mt-2 text-xs text-slate-500 flex items-center justify-center gap-1.5">
              <Info className="w-3.5 h-3.5" /> No resume uploaded — a PDF is auto-generated from your profile details. Preview it with the eye icon above.
            </p>
          )}
        </div>
        {resume.preview && (
          <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-4">
            <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Parsed preview</p>
            <p className="text-xs text-slate-500 whitespace-pre-wrap line-clamp-4 font-mono">{resume.preview}</p>
          </div>
        )}
      </div>

      {/* Details */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 space-y-5">
        <h2 className="text-lg font-semibold">Application Details</h2>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <FieldLabel empty={!form.full_name}>Full Name</FieldLabel>
            <Input data-testid="profile-fullname-input" value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              placeholder="Aarav Sharma" className={inputCls} />
          </div>
          <div>
            <Label className="text-slate-700 text-sm">Email Address</Label>
            <Input data-testid="profile-email-input" value={user?.email || ""} readOnly disabled
              className={`${inputCls} opacity-70 cursor-not-allowed`} />
          </div>
          <div>
            <FieldLabel empty={!form.phone}>Phone Number</FieldLabel>
            <Input data-testid="profile-phone-input" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="+91 98765 43210" className={inputCls} />
          </div>
          <div>
            <FieldLabel empty={!form.headline}>Current Designation</FieldLabel>
            <Input data-testid="profile-headline-input" value={form.headline}
              onChange={(e) => setForm({ ...form, headline: e.target.value })}
              placeholder="Senior Frontend Engineer" className={inputCls} />
          </div>
          <div>
            <FieldLabel empty={!form.current_company}>Current Company</FieldLabel>
            <Input data-testid="profile-company-input" value={form.current_company}
              onChange={(e) => setForm({ ...form, current_company: e.target.value })}
              placeholder="Acme Corp" className={inputCls} />
          </div>
          <div>
            <FieldLabel empty={form.experience_years === ""}>Years of Experience</FieldLabel>
            <Input data-testid="profile-experience-input" type="number" value={form.experience_years}
              onChange={(e) => setForm({ ...form, experience_years: e.target.value })}
              placeholder="5" className={inputCls} />
          </div>
          <div className="sm:col-span-2">
            <FieldLabel empty={!form.portfolio_url}>LinkedIn / Portfolio</FieldLabel>
            <Input data-testid="profile-portfolio-input" value={form.portfolio_url}
              onChange={(e) => setForm({ ...form, portfolio_url: e.target.value })}
              placeholder="https://linkedin.com/in/you" className={inputCls} />
          </div>
          <div>
            <Label className="text-slate-700 text-sm">Location</Label>
            <Input data-testid="profile-location-input" value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder="Bengaluru, IN" className={inputCls} />
          </div>
        </div>

        <div>
          <FieldLabel empty={!form.key_achievement}>Key Achievement</FieldLabel>
          <Textarea data-testid="profile-achievement-input" value={form.key_achievement}
            onChange={(e) => setForm({ ...form, key_achievement: e.target.value })}
            placeholder="e.g. Cut API latency by 40% and led a team of 5 engineers."
            className="mt-1.5 bg-slate-50 border-slate-300 text-slate-900 focus-visible:ring-amber-500/40 min-h-[80px]" />
        </div>

        <div>
          <FieldLabel empty={form.skills.length === 0}>Skills <span className="text-slate-400 font-normal">(first is your Primary Skill)</span></FieldLabel>
          <div className="flex gap-2 mt-1.5">
            <Input data-testid="profile-skill-input" value={skillInput}
              onChange={(e) => setSkillInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSkill(); } }}
              placeholder="Type a skill and press Enter"
              className="bg-slate-50 border-slate-300 text-slate-900 focus-visible:ring-amber-500/40 h-11" />
            <Button onClick={addSkill} variant="outline" className="border-slate-300 h-11" data-testid="add-skill-button">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {form.skills.map((s, i) => (
              <Badge key={s} className={`gap-1 py-1 border ${i === 0 ? "bg-amber-500/15 text-amber-700 border-amber-500/40" : "bg-orange-500/10 text-orange-600 border-orange-500/30"}`}>
                {i === 0 && <span className="text-[9px] font-bold uppercase mr-0.5">Primary</span>}{s}
                <button onClick={() => setForm({ ...form, skills: form.skills.filter((x) => x !== s) })}>
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
          </div>
        </div>

        <Button data-testid="save-profile-button" onClick={save} disabled={saving}
          className="bg-amber-500 text-black hover:bg-amber-400 font-semibold transition-colors">
          {saving ? "Saving…" : <><Check className="w-4 h-4 mr-2" /> Save profile</>}
        </Button>
      </div>
    </div>
  );
}
